import { useEffect, useRef } from "react";

import Button from "@musicfy/components/Button";
import Dialog from "@musicfy/components/Dialog";
import Icon, { type IIconName } from "@musicfy/components/Icon";
import SaleOfferHeading from "@musicfy/components/SaleOfferHeading";
import { useAnalytics } from "@musicfy/libs/AnalyticsProvider";
import { proUpsellCopy } from "@musicfy/libs/PaddleProvider/proUpsell";

const PERKS: { icon: IIconName; label: string }[] = [
  { icon: "rotation", label: "Unlimited generations" },
  { icon: "sound", label: "No watermark, HD" },
  { icon: "cube", label: "Every voice + stems" },
  { icon: "rocket", label: "Commercial license" },
];

export interface IProUpsellProps {
  /** The checkout origin of the wall that was hit (drives the feature copy). */
  reason: string;
  /** Close without opening checkout (esc / backdrop / X / "maybe later"). */
  onDismiss: () => void;
  /** Close and send the user into the pricing flow for this origin. */
  onUpgrade: () => void;
}

/**
 * Pro-Feature upsell interstitial.
 *
 * Shown when a non-eligible user hits a pro feature wall. Leads with feature
 * copy, then Summer Sale soft urgency (no countdown / no WELCOME50). CTA opens
 * pricing — promo codes stay email-deep-link isolated.
 */
const ProUpsell = ({
  reason,
  onDismiss,
  onUpgrade,
}: IProUpsellProps): JSX.Element => {
  const { trackEvent } = useAnalytics();
  const copy = proUpsellCopy(reason);

  const hasTrackedShownRef = useRef(false);

  useEffect(() => {
    if (hasTrackedShownRef.current) {
      return;
    }
    hasTrackedShownRef.current = true;
    trackEvent("Pro Upsell Shown", { reason });
  }, [trackEvent, reason]);

  const handleUpgrade = () => {
    trackEvent("Pro Upsell Upgrade", { reason });
    onUpgrade();
  };

  return (
    <Dialog
      open
      onClose={onDismiss}
      title="Upgrade to unlock"
      TitleIcon={<Icon name="crown" />}
      width="450px"
    >
      <div className="text-12 font-bold uppercase tracking-wide text-pink-200">
        {copy.eyebrow}
      </div>
      <div className="mt-2 text-28 font-bold">
        {copy.headline.charAt(0).toUpperCase() + copy.headline.slice(1)}
      </div>
      <div className="mt-3 font-semibold text-white/80">{copy.sub}</div>

      <SaleOfferHeading
        className="mb-1 mt-5"
        titleClassName="text-16 font-bold"
        extensionClassName="text-13 text-white/80"
      />

      <div className="mt-6 grid grid-cols-1 gap-4 lg:grid-cols-2">
        {PERKS.map((p) => (
          <div key={p.label} className="flex items-center gap-3">
            <Icon name={p.icon} className="w-5" />
            <div>{p.label}</div>
          </div>
        ))}
      </div>

      <div className="mt-8">
        <Button onClick={handleUpgrade} fullWidth>
          Upgrade Now
        </Button>
        <Button onClick={onDismiss} fullWidth style="tertiary" className="mt-3">
          Maybe later
        </Button>
      </div>
    </Dialog>
  );
};

export default ProUpsell;
